import type { Dict } from "../types";

export const clients: Dict = {
  en: {

    // Customer toast messages (errorToast / successToast wrapper)
    "Customer.toast.addToBlacklistFailed": "Could not add the client to the blacklist",
    "Customer.toast.createClientFailed": "Could not create client",
    "Customer.toast.deletePhotoFailed": "Could not delete the photo",
    "Customer.toast.loadFileFailed": "Could not load the client file",
    "Customer.toast.loadInvoiceFailed": "Could not load this invoice",
    "Customer.toast.removeFromBlacklistFailed": "Could not remove the client from the blacklist",
    "Customer.toast.saveFileFailed": "Could not save the client file",
    "Customer.toast.updateClientTypeFailed": "Could not update the client type",
    "Customer.toast.updateLanguageFailed": "Could not update the client's language",
    "Customer.toast.uploadPhotoFailed": "Could not upload the photo",

    // CustomerFile
    "CustomerFile.clientType": "Client Type",
    "CustomerFile.selectClientType": "Select client type",
    "CustomerFile.clientType.new": "New client",
    "CustomerFile.clientType.returning": "Returning client",
    "CustomerFile.clientType.loyal": "Loyal client",
    "CustomerFile.clientType.flagged": "Flagged",
    "CustomerFile.clientType.blacklisted": "Blacklisted",
    "CustomerFile.blacklistedReadOnly": "Remove this client from the blacklist to change their type",

    // Analytics
    "Analytics.searchCustomerPlaceholder": "Search by name, phone, or email...",

    // CustomerFile — redesigned dialog (tabs, KPI, sections)
    "CustomerFile.tab.profile": "Profile",
    "CustomerFile.tab.communication": "Communication",
    "CustomerFile.tab.history": "History",
    "CustomerFile.tab.notes": "Notes & photos",
    "CustomerFile.section.identity": "Identity & contact",
    "CustomerFile.section.identityDesc": "Essential info and enriched client profile.",
    "CustomerFile.verifiedAccount.explain": "This client has their own account. Changing the email here updates salon communications, never their sign in address.",
    "CustomerFile.kpi.reliability": "Reliability",
    "CustomerFile.kpi.spending": "Spending",
    "CustomerFile.daysShort": "d",
    "CustomerFile.histTab.appointments": "Appointments",
    "CustomerFile.histTab.invoices": "Invoices",
    "CustomerFile.histTab.products": "Products",
    "CustomerFile.histTab.giftcards": "Gift cards",
    "CustomerFile.histTab.balance": "Balance",
    "CustomerBalance.total": "Total owed",
    "CustomerBalance.empty": "No amount owed.",
    "CustomerBalance.fromInvoice": "Invoice {number}",
    "CustomerBalance.manual": "Manual entry",
    "CustomerBalance.needsAttribution": "Shared invoice, to be split",
    "CustomerBalance.mismatch": "The invoice changed, the split needs review",
    "CustomerBalance.settle": "Settle",
    "CustomerBalance.split": "Split",
    "CustomerBalance.record": "Record an amount owed",
    "CustomerBalance.recordTitle": "Record an amount owed",
    "CustomerBalance.amount": "Amount",
    "CustomerBalance.reason": "Reason (required)",
    "CustomerBalance.reasonRequired": "Please explain why this amount is owed.",
    "CustomerBalance.amountInvalid": "Enter an amount greater than zero.",
    "CustomerBalance.recording": "Recording…",
    "CustomerBalance.recordSuccess": "Amount owed recorded.",
    "CustomerBalance.recordFailed": "Could not record the amount owed.",
    "CustomerBalance.cancelEntry": "Cancel this amount",
    "CustomerBalance.cancelReason": "Why are you cancelling it?",
    "CustomerBalance.cancelReasonRequired": "Please explain why you are cancelling this amount.",
    "CustomerBalance.confirmCancel": "Confirm cancellation",
    "CustomerBalance.cancelling": "Cancelling…",
    "CustomerBalance.cancelSuccess": "Amount cancelled.",
    "CustomerBalance.cancelFailed": "Could not cancel this amount.",
    "CustomerBalance.settleTitle": "Settle this amount",
    "CustomerBalance.remainingDue": "Amount owed:",
    "CustomerBalance.settling": "Settling…",
    "CustomerBalance.settleSuccess": "Amount settled.",
    "CustomerBalance.settleFailed": "Could not settle this amount.",
    "CustomerBalance.noOpenRegister": "Open the cash register before taking a cash settlement.",
    "CustomerBalance.cashOtherLocation": "This balance belongs to {location}. Settle it with another payment method or from {location}.",
    "CustomerBalance.cashOtherLocationUnnamed": "This balance belongs to another location. Settle it with another payment method or from that location.",
    "CustomerBalance.loadingRetry": "Still loading, try again in a moment.",
    "CustomerBalance.settlementFor": "Balance settlement,",
    "CustomerBalance.settleInProgress": "A settlement is already in progress.",
    "CustomerBalance.invoiceMoneyLoadFailed": "Could not load the invoice's billing data, try again.",
    "CustomerBalance.residualNotCashable": "This residual amount cannot be settled in cash, choose another payment method.",
    "CustomerBalance.alreadySettled": "This line was already settled elsewhere, nothing is left due.",
    "CustomerBalance.shareLoadFailed": "Could not load this customer's share, try again.",
    "CustomerBalance.entryLoadFailed": "Could not load this outstanding amount, try again.",
    "CustomerBalance.orphanCashMovement": "A cash movement was recorded but the settlement did not go through. Correct the drawer by hand, amount:",
    "CustomerBalance.splitTitle": "Split the shared invoice",
    "CustomerBalance.splitDesc": "Invoice {number} serves several customers. Its balance belongs to nobody in particular until you say who owes what.",
    "CustomerBalance.splitInvoiceBalance": "Invoice balance to split",
    "CustomerBalance.splitLinesHint": "Line amounts are shown before taxes, as a guide. The split applies to the invoice balance above.",
    "CustomerBalance.splitShare": "Share owed",
    "CustomerBalance.splitAssigned": "Assigned",
    "CustomerBalance.splitRemainingToAssign": "Left to assign",
    "CustomerBalance.splitUnknownCustomer": "Customer without a name",
    "CustomerBalance.splitAction": "Save the split",
    "CustomerBalance.splitting": "Saving…",
    "CustomerBalance.splitSuccess": "Split saved.",
    "CustomerBalance.splitFailed": "Could not save the split.",
    "CustomerBalance.splitGapNotZero": "The shares must add up to exactly the invoice balance.",
    "CustomerBalance.splitAmountInvalid": "Enter a valid amount for every customer.",
    "CustomerBalance.splitEmpty": "Assign an amount to at least one customer.",
    "CustomerBalance.splitInvoiceGone": "This invoice no longer exists.",
    "CustomerBalance.splitStaleBalance": "The invoice balance changed, start the split over.",
    "CustomerBalance.splitReplaceFailed": "The existing shares could not be cancelled, reopen this window and try again.",
    "CustomerBalance.splitReplaceLost": "The previous shares were cancelled but the new ones could not be saved. This invoice is back to being unsplit, start the split over.",
    "CustomerBalance.splitInProgress": "A split is already being saved.",
    "CustomerBalance.splitRedoneReason": "Split redone",
    "CustomerBalance.creditAvailable": "Available credit",
    "CustomerBalance.creditNeverNets": "A credit is never subtracted from what is owed on its own. Apply it from a line below.",
    "CustomerBalance.grantCredit": "Grant a credit",
    "CustomerBalance.applyCredit": "Apply a credit",

    // ── Lot 2 credit client, tache 6 : depenser un credit sur une facture ──
    "StoreCredit.payWith": "Pay with the customer's credit",
    "StoreCredit.dialogTitle": "Customer credit",
    "StoreCredit.available": "Available credit",
    "StoreCredit.amount": "Amount to apply",
    "StoreCredit.apply": "Apply",
    "StoreCredit.none": "No credit is available for this invoice's customers.",
    "StoreCredit.nothingDue": "This invoice owes nothing, there is nothing to pay with a credit.",
    "StoreCredit.rowLocked": "Paid with a customer credit. This line is removed by cancelling the credit note, not from here.",
    "StoreCredit.staleCredit": "This credit changed since this window was opened. Reopen it and enter the amount again.",
    "StoreCredit.loadFailed": "The credit could not be read again. Nothing was applied.",
    "StoreCredit.selectCredit": "Choose a credit first.",
    "StoreCredit.amountInvalid": "Enter an amount above zero.",
    "StoreCredit.saveInvoiceFirst": "Save the invoice before applying a credit to it.",
    "StoreCredit.applied": "Credit applied.",
    "StoreCredit.applyFailed": "The credit could not be applied.",
    "StoreCredit.expiresOn": "Expires on",
    "StoreCredit.error.accessDenied": "You cannot act on this location.",
    "StoreCredit.error.permissionDenied": "You do not have permission to manage customer credits.",
    "StoreCredit.error.amountInvalid": "Enter an amount above zero.",
    "StoreCredit.error.notFound": "This credit no longer exists.",
    "StoreCredit.error.otherBrand": "This credit belongs to another business.",
    "StoreCredit.originSalon": "Credit issued at {salon}",
    "StoreCredit.error.deleted": "This credit was deleted.",
    "StoreCredit.error.notStoreCredit": "This credit note was refunded, there is nothing left to spend.",
    "StoreCredit.error.notSpendable": "This credit cannot be spent in its current state.",
    "StoreCredit.error.expired": "This credit has expired.",
    "StoreCredit.error.insufficient": "The available credit does not cover that amount. Enter a smaller amount.",
    "StoreCredit.error.aboveRemaining": "This credit only holds {amount}. Enter that amount or less.",
    "StoreCredit.error.originInvoice": "This credit came from this very invoice. Spending it here would make it disappear without giving the customer anything.",
    "StoreCredit.error.invoiceNotFound": "This invoice could not be found.",
    "StoreCredit.error.customerMismatch": "This invoice carries no line for the customer this credit belongs to.",
    "StoreCredit.error.exceedsInvoiceDue": "The amount is above what this invoice still owes.",
    "StoreCredit.error.exceedsCustomerShare": "A store credit is nominative: it only settles its own customer's share of this invoice. Enter a smaller amount.",
    "StoreCredit.customerShareHint": "{name}'s share of this invoice: {amount}",
    "StoreCredit.error.manualPaymentRefused": "A customer credit payment cannot be entered by hand. Use the customer credit button.",
    "StoreCredit.error.lineImmutable": "A customer credit payment line cannot be edited. Remove the line, or cancel the credit note.",
    "StoreCredit.error.reversalLocked": "This line is a credit note reversal. It is removed by cancelling the credit note, not by deleting the payment.",
    "StoreCredit.error.backsSettlement": "This credit settled an amount owed on the customer's file. Removing its payment would give the credit back without cancelling the settlement.",
    "StoreCredit.error.entryNotFound": "This amount owed no longer exists.",
    "StoreCredit.error.entryOtherSalon": "This amount owed belongs to another salon.",
    "StoreCredit.error.entryCancelled": "This amount owed was cancelled, there is nothing left to settle.",
    "StoreCredit.error.entryDerived": "This amount comes from a whole invoice. Open the invoice and apply the credit there.",
    "StoreCredit.error.entryCustomerMismatch": "This amount is owed by another customer than the one this credit belongs to.",
    "StoreCredit.error.exceedsDue": "The amount is above what is still owed on this line. Enter a smaller amount.",
    "StoreCredit.error.goodwillCustomerNotFound": "This customer file could not be found.",
    "StoreCredit.error.goodwillCustomerNotInSalon": "This customer file has no invoice and no appointment in your salon. A credit cannot be granted on it.",
    // Lot A du 2026-09-27, tâche 9. Ce message a CHANGÉ DE REFUS : l'ancien
    // `CREDIT_NOTE_NO_CUSTOMER` a disparu à la tâche 7, remplacé par
    // `credit_note_store_credit_needs_customer`, qui ne refuse plus que la
    // destination « crédit au dossier ». Rendre l'argent reste possible, et le
    // message le dit maintenant.
    "StoreCredit.error.noCustomerOnLines": "None of the selected lines carries a customer, so there is no file to credit. Choose \"Give the money back\", or add the customer on the invoice.",
    "StoreCredit.error.customerNotOnLines": "This file does not appear on any of the selected lines. Select the lines of the customer to credit.",
    "StoreCredit.error.cashRefundNeedsOpenRegister": "No cash register is open, and a cash refund would leave no trace in the drawer. Open the register, or choose card or transfer, or credit the customer's file.",
    "StoreCredit.error.lineAlreadyCredited": "One of the selected lines has already been credited. Uncheck it: a line can only be credited once.",
    "StoreCredit.error.tipOnlyOnFull": "The tip can only be refunded with the credit note that credits all remaining lines, and only once.",
    "StoreCredit.error.exceedsCollected": "This credit note would return more than the invoice collected. Maximum: {max}.",
    "StoreCredit.error.onlineRefundPending": "An online refund is still being processed on this invoice. Try again in a few minutes.",
    "StoreCredit.error.multipleCustomers": "The selected lines carry several customers. A credit note lands on one file only, select the lines of a single customer.",
    "StoreCredit.error.alreadyCancelled": "This credit note is already cancelled.",
    "StoreCredit.error.alreadyUsed": "This credit was already partly spent, it can no longer be cancelled.",
    "StoreCredit.error.cancelReasonRequired": "Cancelling a credit requires a written reason.",
    "StoreCredit.error.undoReasonRequired": "Undoing a credit application requires a written reason.",
    "StoreCredit.error.redemptionNotFound": "This credit application no longer exists.",
    "StoreCredit.error.redemptionOtherSalon": "This credit application belongs to another salon.",
    "StoreCredit.error.redemptionNotAUse": "Only a credit that was spent can be undone, not a restoration.",
    "StoreCredit.error.redemptionAlreadyRestored": "This application has already been undone, the credit is back on the file.",
    "StoreCredit.error.redemptionLinkMissing": "This application carries no payment and no settlement to undo. It was recorded before this feature existed, so it has to be corrected by hand.",
    "StoreCredit.error.entryBelowSettled": "This amount owed is already settled above the new figure. Undo a settlement first.",
    "CustomerCredits.title": "Credit on this file",
    "CustomerCredits.empty": "This file has never carried any store credit.",
    "CustomerCredits.ofTotal": "of {total} granted",
    "CustomerCredits.applications": "Applied to",
    "CustomerCredits.undo": "Undo",
    "CustomerCredits.undoTitle": "Undo this credit application",
    "CustomerCredits.undoDescription": "{amount} goes back onto the file, and the amount it settled becomes owed again.",
    "CustomerCredits.undoWarning": "The invoice or the amount owed that this credit settled goes back up. The credit note keeps a permanent trace of this reversal, with your reason.",
    "CustomerCredits.undoing": "Undoing…",
    "CustomerCredits.undoSuccess": "Credit application undone.",
    "CustomerCredits.undoFailed": "Could not undo this credit application.",
    "CustomerCredits.cancel": "Cancel this credit",
    "CustomerCredits.cancelTitle": "Cancel this credit",
    "CustomerCredits.cancelDescription": "The whole {amount} is taken back from the file.",
    "CustomerCredits.cancelWarning": "The reversal line on the original invoice is removed, and any stock this credit put back is taken out again. This cannot be undone.",
    "CustomerCredits.cancelling": "Cancelling…",
    "CustomerCredits.cancelSuccess": "Credit cancelled.",
    "CustomerCredits.cancelFailed": "Could not cancel this credit.",
    "CustomerCredits.cancelOtherLocation": "Issued at {salon}: it can be cancelled from there.",
    "CustomerCredits.undoOtherLocation": "Applied at {salon}: it can be undone from there.",
    "CustomerCredits.cancelBlocked": "This credit was partly spent. Undo its applications first, or record what is owed as a manual amount.",
    "CustomerCredits.reasonRequired": "Write down why.",
    "CustomerCredits.reasonPlaceholder": "Reason (required)",
    "CustomerCredits.hidden": "Credit on this file is only visible with access to the Invoicing module or to client files.",
    "CustomerCredits.notUndoable": "Cannot be undone",
    "GrantCredit.title": "Grant a credit to this file",
    "GrantCredit.description": "This money is offered. It stays on the customer's file and will be spent on a later invoice.",
    "GrantCredit.amount": "Amount to grant",
    "GrantCredit.reason": "Reason",
    "GrantCredit.details": "Justification (required)",
    "GrantCredit.detailsRequired": "Explain why this credit is granted.",
    "GrantCredit.amountInvalid": "Enter an amount greater than zero.",
    "GrantCredit.submit": "Grant the credit",
    "GrantCredit.submitting": "Granting…",
    "GrantCredit.success": "Credit granted.",
    "GrantCredit.failed": "Could not grant the credit.",
    "GrantCredit.expiresAt": "Expiry date",
    "GrantCredit.expiresNever": "No expiry",
    "GrantCredit.expiresWarning": "Store credit granted here never expires, on purpose. A credit issued from a return is very close to a prepaid card, and Quebec consumer protection law frames their expiry tightly. This field will only open once that has been checked with a lawyer.",
    "ApplyCredit.title": "Apply a credit to this amount",
    "ApplyCredit.none": "This customer has no credit left to spend.",
    "ApplyCredit.nothingDue": "This line owes nothing, there is nothing to apply a credit to.",
    "ApplyCredit.applying": "Applying…",
    "ApplyCredit.success": "Credit applied to the amount owed.",
    "ApplyCredit.failed": "Could not apply the credit.",
    // CustomerFile Reliability
    "CustomerFile.section.reliability": "History & Reliability",
    "CustomerFile.totalAppointments": "Total Appointments",
    "CustomerFile.presentCount": "Present",
    "CustomerFile.noshowCount": "No-show",
    "CustomerFile.reliabilityRate": "Reliability rate",
    "CustomerFile.totalSpending": "Total Spending",
    "CustomerFile.averageBasket": "Avg. Basket",
    "CustomerFile.visitFrequency": "Visit Frequency",
    "CustomerFile.weeksAbbr": "w",
    "CustomerFile.lastVisit": "Last visit",
    "CustomerFile.daysAgo": "days ago",
    "CustomerFile.today": "today",
    "CustomerFile.never": "Never",
    "CustomerFile.inactiveWarning": "Inactive > 3 months",
    "Customer.lastVisitFilter.label": "Last visit",
    "Customer.lastVisitFilter.all": "All",
    "Customer.lastVisitFilter.7d": "Last 7 days",
    "Customer.lastVisitFilter.30d": "Last 30 days",
    "Customer.lastVisitFilter.90d": "Last 90 days",
    "Customer.lastVisitFilter.6m": "Last 6 months",
    "Customer.lastVisitFilter.1y": "Last year",
    "Customer.lastVisitFilter.2y": "Last 2 years",
    "Customer.lastVisitFilter.5y": "Last 5 years",
    "Customer.lastVisitFilter.10y": "Last 10 years",
    "Customer.lastVisitFilter.never": "Never visited",
    "Customer.statusFilter.label": "Status",
    "Customer.statusFilter.all": "All statuses",
    "Customer.statusFilter.blacklisted": "Blacklisted",
    "Analytics.noClientsMatchFilter": "No client matches this filter.",
    "CustomerFile.autoClassified": "(auto-calculated)",
    "CustomerFile.phoneNotProvided": "No phone",
    "CustomerFile.phoneActions.menuLabel": "Phone number actions",
    "CustomerFile.phoneActions.call": "Call",
    "CustomerFile.phoneActions.text": "Text",
    "CustomerFile.phoneActions.copy": "Copy number",
    "CustomerFile.phoneActions.copied": "Number copied",
    "CustomerFile.phoneActions.copyFailed": "Could not copy the number",
    "CustomerFile.smsNotDelivered": "This number does not receive text messages",
    "CustomerFile.contactMasked": "Contact info hidden",

    // Customer Files
    "CustomerFile.loading": "Loading...",
    "CustomerFile.section.essential": "Essential Information",
    "CustomerFile.section.safety": "Safety & Personalization",
    "CustomerFile.section.technicalSheet": "Technical Sheet",
    "CustomerFile.section.history": "Visual Tracking",
    "CustomerFile.section.notes": "Notes",
    "CustomerFile.allergies": "Allergies / Sensitivities",
    "CustomerFile.allergiesPlaceholder": "e.g., PPD, fragrance, latex...",
    "CustomerFile.preferredStylist": "Preferred Employee",
    "CustomerFile.selectStylist": "Select employee",
    "CustomerFile.none": "None",
    "CustomerFile.technicalSheet.add": "Entry",
    "CustomerFile.technicalSheet.empty": "No technical sheet entries yet",
    "CustomerFile.technicalSheet.date": "Date",
    "CustomerFile.technicalSheet.employee": "Employee",
    "CustomerFile.technicalSheet.selectEmployee": "Select an employee",
    "CustomerFile.technicalSheet.note": "Note",
    "CustomerFile.technicalSheet.notePlaceholder": "Describe the technical service, formula, result...",
    "CustomerFile.technicalSheet.save": "Save entry",
    "CustomerFile.technicalSheet.lockedHint": "A saved entry can no longer be edited.",
    "CustomerFile.technicalSheet.addedOn": "Added on",
    "CustomerFile.technicalSheet.migrated": "Migrated",
    "CustomerFile.technicalSheet.delete": "Delete",
    "CustomerFile.technicalSheet.deleteConfirm": "Delete this entry permanently? This action cannot be undone.",
    "CustomerFile.technicalSheet.saved": "Entry saved",
    "CustomerFile.technicalSheet.deleted": "Entry deleted",
    "CustomerFile.technicalSheet.saveFailed": "Could not save the entry",
    "CustomerFile.technicalSheet.deleteFailed": "Could not delete the entry",
    "CustomerFile.technicalSheet.employeeRequired": "Please select an employee.",
    "CustomerFile.technicalSheet.noteRequired": "The note cannot be empty.",
    "CustomerFile.technicalSheet.selectDate": "Select a date",
    "CustomerFile.technicalSheet.edit": "Edit note",
    "CustomerFile.technicalSheet.editSave": "Save correction",
    "CustomerFile.technicalSheet.edited": "Edited on",
    "CustomerFile.technicalSheet.editedBy": "by",
    "CustomerFile.technicalSheet.editSaved": "Sheet corrected",
    "CustomerFile.technicalSheet.editFailed": "Could not correct the sheet",
    "CustomerFile.preferredProducts": "Preferred Products / Brands",
    "CustomerFile.preferredProductsPlaceholder": "Brands they love or want to avoid...",
    "CustomerFile.hairPhotos": "Client Photos (Max 6)",
    "CustomerFile.uploadPhoto": "Upload Photo",
    "CustomerFile.uploading": "Uploading...",
    "CustomerFile.photoUploaded": "Photo uploaded successfully",
    "CustomerFile.photoDeleted": "Photo deleted successfully",
    "CustomerFile.maxPhotos": "Maximum 6 photos allowed",
    "CustomerFile.pendingPhoto": "Photo to be saved",
    "CustomerFile.pendingPhotoRemove": "Remove this photo",
    "CustomerFile.photoRemove": "Remove this photo",
    "CustomerFile.appointmentHistory": "Appointment History",
    "CustomerFile.noAppointments": "No previous appointments",
    "CustomerFile.productPurchaseHistory": "Product Purchase History",
    "CustomerFile.invoiceHistory": "Invoice History",
    "CustomerFile.noInvoices": "No invoices recorded.",
    "CustomerFile.invoiceItemsMore": "+{count} more",
    "CustomerFile.giftCardHistory": "Gift Cards",
    "CustomerFile.noGiftCards": "No gift cards linked to this client.",
    "CustomerFile.beneficiary": "Beneficiary",
    "CustomerFile.noProductPurchases": "No product purchases recorded.",
    "CustomerFile.commPreferences": "Communication Preferences",
    "CustomerFile.smsNotifications": "SMS Notifications",
    "CustomerFile.emailNotifications": "Email Notifications",
    "CustomerFile.optOutMarketing": "Opt out of Marketing",
    "CustomerFile.disableAllNotifications": "Disable all notifications",
    "CustomerFile.disableAllNotificationsHelp": "No email or text message will be sent to this client, including appointment confirmations and reminders.",
    "CustomerFile.marketing.section": "Marketing / Newsletter",
    "CustomerFile.marketing.emailLabel": "Receive marketing emails",
    "CustomerFile.marketing.helper": "Independent from appointment notifications",
    "Blacklist.title": "Blacklist",
    "Blacklist.blockedClients": "Blocked clients",
    "Blacklist.addClient": "Add a client to the blacklist",
    "Blacklist.removeClient": "Remove",
    "Blacklist.removeConfirm": "Remove this client from the blacklist?",
    "Blacklist.removeConfirmDescription": "This client will be able to book online again.",
    "Blacklist.reason": "Reason (optional)",
    "Blacklist.reasonPlaceholder": "E.g.: Never shows up to appointments",
    "Blacklist.addedOn": "Added on",
    "Blacklist.employeeReadOnly": "Only an owner can modify this list",
    "Blacklist.searchClient": "Search for a client...",
    "Blacklist.bookingBlocked": "Online booking is disabled. If you'd like to make an appointment, please contact us at the salon.",
    "Blacklist.noClients": "No clients in the blacklist",
    "Blacklist.emptyDescription": "Add clients to prevent them from booking online",
    "Blacklist.confirmAdd": "Add to blacklist",
    "Blacklist.adding": "Adding...",
    "Blacklist.clientAdded": "Client added to the blacklist",
    "Blacklist.clientRemoved": "Client removed from the blacklist",
    "Blacklist.blockedCountOne": "{count} blocked client",
    "Blacklist.blockedCountMany": "{count} blocked clients",
    "Blacklist.pickClientFirst": "Choose a client first.",
    "Blacklist.removing": "Removing...",
    "CustomerFile.notifDialog.title.email": "Email Notification Preferences",
    "CustomerFile.notifDialog.title.sms": "SMS Notification Preferences",
    "CustomerFile.notifDialog.selectAll": "Select All",
    "CustomerFile.notifDialog.section.appointments": "Appointments",
    "CustomerFile.notifDialog.section.series": "Recurring Series",
    "CustomerFile.notifDialog.section.group": "Multi-Service",
    "CustomerFile.notifDialog.section.reminders": "Reminders",
    "CustomerFile.notifDialog.appointments.pending": "Pending request",
    "CustomerFile.notifDialog.appointments.confirmation": "Approval of pending request",
    "CustomerFile.notifDialog.appointments.book": "Instant booking confirmation",
    "CustomerFile.notifDialog.appointments.reschedule": "Rescheduled",
    "CustomerFile.notifDialog.appointments.cancel": "Cancelled",
    "CustomerFile.notifDialog.appointments.duration_change": "Duration changed",
    "CustomerFile.notifDialog.series.pending": "Series pending",
    "CustomerFile.notifDialog.series.approved": "Series approved",
    "CustomerFile.notifDialog.series.rejected": "Series unavailable",
    "CustomerFile.notifDialog.group.pending": "Multi-service pending",
    "CustomerFile.notifDialog.group.approved": "Multi-service confirmed",
    "CustomerFile.notifDialog.reminders.24h": "24-hour reminder",
    "CustomerFile.notifDialog.reminders.48h": "48-hour reminder",
    "CustomerFile.notifDialog.reminders.72h": "72-hour reminder",
    "CustomerFile.notifDialog.reminders.1h": "1-hour reminder",
    "CustomerFile.notifDialog.confirm": "Confirm",
    "CustomerFile.internalNotes": "Internal Notes (Staff Only)",
    "CustomerFile.internalNotesPlaceholder": "Private notes for staff...",
    "CustomerFile.save": "Save",
    "CustomerFile.cancel": "Cancel",
    "CustomerFile.saving": "Saving...",
    "CustomerFile.success": "Success",
    "CustomerFile.fileUpdated": "Customer file updated",
    "CustomerFile.error": "Error",
    "CustomerFile.invalidImageFile": "Please select a valid image file",
    "CustomerFile.fileTooLarge": "File size must be less than 5MB",
    "CustomerFile.service": "Service",
    "CustomerFile.status.confirmed": "Confirmed",
    "CustomerFile.status.completed": "Completed",
    "CustomerFile.status.cancelled": "Cancelled",
    "CustomerFile.status.pending": "Pending",
    "CustomerFile.loadMore": "Load more",
    "CustomerFile.remaining": "remaining",
    "CustomerFile.remainingAppointments": "appointments remaining",
    "CustomerFile.cancelledHidden": "cancelled appointments hidden",
    // Rappel des prochains rendez-vous, remis ou envoyé à la cliente au comptoir.
    "CustomerFile.upcoming.button": "Upcoming appointments",
    "CustomerFile.upcoming.print": "Print",
    "CustomerFile.upcoming.email": "Send by email",
    "CustomerFile.upcoming.dialogTitle": "Send upcoming appointments",
    "CustomerFile.upcoming.recipient": "Recipient",
    "CustomerFile.upcoming.addressOnFile": "Address on file",
    "CustomerFile.upcoming.maskedHint": "You do not have access to this client's address. The reminder will go to the address on file.",
    "CustomerFile.upcoming.mutedHint": "This client turned off emails. You asked for this reminder, so it will still be sent.",
    "CustomerFile.upcoming.attachmentsNote": "Each appointment is attached as a calendar file, so the client can add it in one tap.",
    "CustomerFile.upcoming.cancel": "Cancel",
    "CustomerFile.upcoming.send": "Send",
    "CustomerFile.upcoming.sending": "Sending",
    "CustomerFile.upcoming.sent": "Reminder sent",
    "CustomerFile.upcoming.sendFailed": "The reminder could not be sent",
    "CustomerFile.upcoming.preparing": "Preparing",
    "CustomerFile.upcoming.none": "This client has no upcoming appointments",
    "CustomerFile.upcoming.printFailed": "Nothing reached the printer",
    // Enriched profile fields (Task 7)
    "CustomerFile.emergencyContact": "Emergency Contact",
    "CustomerFile.consent.section": "Law 25: Data Consent",
    "CustomerFile.consent.section.generic": "Data collection consent",
    "CustomerFile.consent.label": "Consent obtained from client",
    "CustomerFile.consent.dateLabel": "Consent date",
    "CustomerFile.consent.collectedOn": "Collected on {date} by {who}",
    "CustomerFile.familyTitle": "Family",
    "CustomerFile.roleConjoint": "Partner",
    "CustomerFile.roleEnfant": "Child",
    "CustomerFile.roleParent": "Parent",
    "CustomerFile.roleFratrie": "Sibling",
    "CustomerFile.roleGrandparent": "Grandparent",
    "CustomerFile.roleGrandchild": "Grandchild",
    "CustomerFile.roleAutre": "Other family member",
    "CustomerFile.addMember": "Add a member",
    "CustomerFile.selectClient": "Select a client...",
    "CustomerFile.selectRelationship": "Relationship...",
    "CustomerFile.alreadyLinked": "This link already exists",
    "CustomerFile.removeMember": "Remove this member",
    "CustomerFile.familyEmpty": "No family member linked",
    "CustomerFile.familyEmptyHelp": "Link relatives so their appointments and files are easy to find from one another.",
    "CustomerFile.familyEmptyReadOnly": "No relatives are linked to this client. Only someone allowed to manage the directory can add them.",
    "CustomerFile.familyPickClient": "Choose a client first.",
    "CustomerFile.familyPickRole": "Choose the relationship first.",
    "CustomerFile.familyPending": "To be saved",
    "CustomerFile.familySearchPlaceholder": "Search by name, phone or email...",

    // Analytics
    "Analytics.title": "Analytics",
    "Analytics.revenue": "Revenue",
    "Analytics.totalRevenue": "Total Revenue",
    "Analytics.completedAppointment": "appointment scheduled",
    "Analytics.completedAppointments": "appointments scheduled",
    "Analytics.averagePerAppointment": "Average per Appointment",
    "Analytics.cancellationLosses": "Cancellation Losses",
    "Analytics.cancelledSingular": "cancelled",
    "Analytics.cancelled": "cancelled",
    "Analytics.servicePerformance": "Service Performance",
    "Analytics.customerBase": "Customer Base",
    "Analytics.preferredClients": "Client list",
    "Analytics.clientsWhoChose": "Clients who chose this salon",
    "Analytics.allClients": "All Clients Who Have Chosen This Salon",
    "Analytics.noClientsYet": "No client in this salon yet.",
    "Analytics.noCustomersFound": "No customers found.",
    "Analytics.loadFailed": "The client list could not be loaded.",
    "Analytics.retry": "Try again",
    "Analytics.directorySubtitleOne": "{count} client in this salon",
    "Analytics.directorySubtitleMany": "{count} clients in this salon",
    "Analytics.directorySubtitleOneAllLocations": "{count} client across all your locations",
    "Analytics.directorySubtitleManyAllLocations": "{count} clients across all your locations",
    "Analytics.directorySubtitleOneAt": "{count} client attached to {location}",
    "Analytics.directorySubtitleManyAt": "{count} clients attached to {location}",
    "Analytics.directorySubtitleAtUnknown": "Clients attached to {location}",
    "Analytics.directorySubtitleFilteredOne": "{count} client matches this filter",
    "Analytics.directorySubtitleFilteredMany": "{count} clients match this filter",
    "Analytics.pagerPrevious": "Previous page",
    "Analytics.pagerNext": "Next page",
    "Analytics.pagerPage": "Page {page}",
    "Analytics.pagerRange": "{from} to {to} of {total}",
    "Analytics.name": "Name",
    "Analytics.noName": "No name",
    "Analytics.clientsModuleSubtitle": "Manage your clientele and files",
    "Analytics.kpiTotalClients": "Total clients",
    "Analytics.kpiTotalClientsBrand": "Total clients, brand wide",
    "Analytics.kpiLoyal": "Loyal",
    "Analytics.kpiFlagged": "Flagged",
    "Analytics.email": "Email",
    "Analytics.phone": "Phone",
    "Analytics.viewFile": "View File",
    "Analytics.close": "Close",
    "Analytics.thisWeek": "This week",
    "Analytics.thisMonth": "This month",
    "Analytics.custom": "Custom",
    "Analytics.periodLabel": "Period",
    "Analytics.selectStartDate": "Start date",
    "Analytics.selectEndDate": "End date",

    // Add Client Dialog
    "AddClient.title": "Add Client",
    "AddClient.description": "Create a client profile to allow appointments before they create their own account.",
    "AddClient.addButton": "Add",
    "AddClient.addFirstClient": "Add your first client",
    "AddClient.fullName": "Full Name",
    "AddClient.email": "Email",
    "AddClient.phone": "Phone",
    "AddClient.cancel": "Cancel",
    "AddClient.create": "Create Profile",
    "AddClient.errorNameRequired": "Name is required",
    "AddClient.errorNameTooShort": "Name must be at least 2 characters",
    "AddClient.errorEmailRequired": "Email is required",
    "AddClient.errorEmailInvalid": "Invalid email",
    "AddClient.errorPhoneRequired": "Phone is required",
    "AddClient.errorPhoneTooShort": "Phone must be at least 10 digits",
    "AddClient.errorPhoneInvalid": "The phone number is invalid",
    "AddClient.emailHint": "Required for email reminders",
    "AddClient.phoneHint": "Required for text message reminders",
    "AddClient.noContactWarning": "Without an email address or a phone number, this client will not receive any notification (confirmations, reminders).",
    "AddClient.errorEmailTaken": "This email address is already used by {name} in this salon.",
    "AddClient.createAnyway": "Create anyway",
    "AddClient.duplicatePhoneTitle": "Existing phone number",
    "AddClient.duplicatePhoneBodyOne": "The number {phone} is already used by \"{name}\". Create a new profile with this number anyway? (For example, for a family member.)",
    "AddClient.duplicatePhoneBodyMany": "The number {phone} is already used by {count} clients (including \"{name}\"). Create a new profile with this number anyway?",
    "AddClient.duplicateEmailTitle": "Existing email address",
    "AddClient.duplicateEmailBodyOne": "The address {email} is already used by \"{name}\". Create a new profile with this address anyway?",
    "AddClient.duplicateEmailBodyMany": "The address {email} is already used by {count} clients (including \"{name}\"). Create a new profile with this address anyway?",
    "AddClient.errorNotAuthenticated": "You must be logged in",
    "AddClient.errorDuplicate": "A client with this email already exists for your salon",
    "AddClient.errorGeneric": "An error occurred while creating the profile",
    "AddClient.errorNoPermission": "You do not have permission to add a client. Ask the salon owner for it.",
    "AddClient.successCreated": "Client profile created successfully",
    "AddClient.badgeCreatedByYou": "Created by you",
    "AddClient.badgeVerified": "Verified Account",
    "AddClient.editClient": "Edit Client",
    "AddClient.editClientDesc": "Update client information",
    "AddClient.update": "Update",
    "AddClient.updating": "Updating...",
    "AddClient.updateSuccess": "Client updated successfully",
    "AddClient.updateSuccessDesc": "The client information has been updated",
    "AddClient.updateError": "Update failed",
    "AddClient.deleteConfirmTitle": "Delete Client?",
    "AddClient.deleteConfirmDesc": "Are you sure you want to delete this client? This action cannot be undone.",
    "AddClient.deleteConfirm": "Delete",
    "AddClient.deleteCancel": "Cancel",
    "AddClient.deleteSuccess": "Client deleted",
    "AddClient.deleteSuccessDesc": "The client has been removed",
    "AddClient.deleteError": "Deletion failed",
    "AddClient.deleteErrorHasRecords": "Cannot delete",
    "AddClient.deleteErrorHasRecordsDesc": "This client has associated invoices or appointments. Please delete those first.",
    "AddClient.deleteErrorForbidden": "You do not have permission to delete a client.",
    "AddClient.deleteErrorRetry": "The server did not respond. Please try again.",
    "AddClient.fullNamePlaceholder": "Enter full name",
    "AddClient.phonePlaceholder": "Enter phone number",
    "AddClient.emailPlaceholder": "Enter email address",
    "AddClient.googleEmailReadonly": "This email is linked to a Google account and cannot be modified.",
    "AddClient.language": "Language",
    "AddClient.languagePlaceholder": "Select language",
    "AddClient.languageFr": "French",
    "AddClient.languageEn": "English",
    "AddClient.birthday": "Birthday",
    "AddClient.birthdayDay": "Day",
    "AddClient.birthdayMonth": "Month",
    "AddClient.birthdayNone": "Not set",
    "AddClient.errorBirthdayIncomplete": "Choose both the day and the month of the birthday.",
    "AddClient.errorBirthdayImpossible": "This day does not exist in the chosen month.",
    "AddClient.gender": "Gender",
    "AddClient.genderPlaceholder": "Select gender",
    "AddClient.genderMale": "Man",
    "AddClient.genderFemale": "Woman",
    "AddClient.genderOther": "Other",
    "AddClient.genderUndisclosed": "Prefer not to say",
    "AddClient.secondaryPhone": "Secondary Phone",
    "AddClient.secondaryPhoneInvalid": "The secondary phone number is invalid",
    "AddClient.emergencyContactName": "Emergency Contact Name",
    "AddClient.emergencyContactPhone": "Emergency Contact Phone",
    "AddClient.emergencyContactPhoneInvalid": "The emergency contact phone number is invalid",
    "AddClient.emergencyContactRelation": "Relationship",
    "AddClient.acquisitionSource": "How did they hear about the salon?",
    "AddClient.acquisitionSourcePlaceholder": "Select source",
    "AddClient.acquisitionSourceGoogle": "Google",
    "AddClient.acquisitionSourceSocial": "Social media",
    "AddClient.acquisitionSourceReferral": "Referral",
    "AddClient.acquisitionSourceWalkin": "Walk-in",
    "AddClient.acquisitionSourceOnlineBooking": "Online booking",
    "AddClient.acquisitionSourceCompetitor": "Former customer (competitor)",
    "AddClient.acquisitionSourceOther": "Other",
    "AddClient.acquisitionSourceOtherPlaceholder": "Specify...",
    "AddClient.consentLabel": "Client consented to data collection (Law 25)",
    "AddClient.consentLabel.generic": "Client consented to data collection",
    "AddClient.consentDate": "Consent date",

    "Analytics.voiceAIMetrics": "Voice AI Metrics",
    "Analytics.upgradeForAIMetrics": "Upgrade to Enterprise plan to access AI Voice Metrics",
    "Analytics.totalAICalls": "Total AI Calls",
    "Analytics.aiSuccessRate": "Success Rate",
    "Analytics.aiBookings": "AI Bookings",
    "Analytics.aiReschedules": "AI Reschedules",
    "Analytics.aiCancellations": "AI Cancellations",
    "Analytics.onlineMetrics": "Online Metrics",
    "Analytics.onlineBookings": "Online Bookings",
    "Analytics.onlineReschedules": "Online Reschedules",
    "Analytics.onlineCancellations": "Online Cancellations",
    "Analytics.directBookingMetrics": "Direct Booking Metrics (QR + Link)",
    "Analytics.directTotalVisits": "Total Visits",
    "Analytics.directTotalScans": "Total Link Clicks",
    "Analytics.directConversions": "Conversions",
    "Analytics.directConversionRate": "Conversion Rate",
    "Analytics.qrMetrics": "QR Code Metrics",
    "Analytics.qrTotalScans": "Total Scans",
    "Analytics.qrConversions": "Conversions",
    "Analytics.qrConversionRate": "Conversion Rate",
    "Analytics.aiCallAnalytics": "AI Call Analytics",
    "Analytics.totalCalls": "Total Calls",
    "Analytics.successRate": "Success Rate",
    "Analytics.avgCallDuration": "Avg. Call Duration",
    "Analytics.seconds": "seconds",
    "Analytics.totalCallTime": "Total Call Time",

    // Duplicate customers
    "Duplicates.bannerTitle": "It looks like there are {count} duplicate(s).",
    "Duplicates.bannerAction": "Check",
    "Duplicates.reviewTitle": "Duplicate Review",
    "Duplicates.pairCount": "Pair {current} of {total}",
    "Duplicates.matchEmail": "Same email",
    "Duplicates.matchPhone": "Same phone",
    "Duplicates.matchName": "Same name",
    "Duplicates.appointment": "appointment",
    "Duplicates.appointments": "appointments",
    "Duplicates.lastVisit": "Last visit:",
    "Duplicates.unknownName": "Unknown",
    "Duplicates.master": "Master",
    "Duplicates.merge": "Merge",
    "Duplicates.notDuplicate": "Not a duplicate",
    "Duplicates.skip": "Skip",
    "Duplicates.chooseMaster": "Choose the master record",
    "Duplicates.chooseMasterDesc": "The duplicate's appointments and notes will be transferred to the master. The duplicate file will be deleted.",
    "Duplicates.mergeWarning": "This action is irreversible. The duplicate customer file will be permanently deleted after merging.",
    "Duplicates.confirmMerge": "Confirm merge",
    "Duplicates.mergeSuccess": "Customers merged",
    "Duplicates.mergeSuccessDesc": "The duplicate has been merged successfully.",
    "Duplicates.mergeOtherBrand": "This client also has history at another salon, so the files cannot be merged.",
    "Customer.toast.notInBrand": "This client file does not belong to your salon. No change was saved.",
    "Duplicates.dismissed": "Marked as not a duplicate",
    "Duplicates.noPending": "No duplicates to review",
    "Duplicates.noPendingDesc": "All potential duplicates have been reviewed. You can run a new scan to check again.",
    "Duplicates.scanComplete": "Scan complete",
    "Duplicates.findButton": "Find duplicates",
    "Duplicates.scanRunning": "Scanning…",
    "Duplicates.scanStartedHint": "The scan usually takes less than a minute. You can keep working.",
    "Duplicates.scanResultCount": "{count} potential duplicate(s) found.",
    "Duplicates.scanResultNone": "No duplicates found. Your client list is clean.",
    "Duplicates.scanResultReview": "Review",
    "Duplicates.scanFailed": "The scan could not complete. Please try again in a few minutes.",
    "Duplicates.dialogTitle": "Duplicate Customers",
  },
  fr: {

    // Customer toast messages (errorToast / successToast wrapper)
    "Customer.toast.addToBlacklistFailed": "Impossible d'ajouter le client à la liste noire",
    "Customer.toast.createClientFailed": "Impossible de créer le client",
    "Customer.toast.deletePhotoFailed": "Impossible de supprimer la photo",
    "Customer.toast.loadFileFailed": "Impossible de charger la fiche client",
    "Customer.toast.loadInvoiceFailed": "Impossible de charger cette facture",
    "Customer.toast.removeFromBlacklistFailed": "Impossible de retirer le client de la liste noire",
    "Customer.toast.saveFileFailed": "Impossible d'enregistrer la fiche client",
    "Customer.toast.updateClientTypeFailed": "Impossible de mettre à jour le type de client",
    "Customer.toast.updateLanguageFailed": "Impossible de mettre à jour la langue du client",
    "Customer.toast.uploadPhotoFailed": "Impossible de téléverser la photo",

    // CustomerFile
    "CustomerFile.clientType": "Type de client",
    "CustomerFile.selectClientType": "Sélectionner le type de client",
    "CustomerFile.clientType.new": "Nouveau client",
    "CustomerFile.clientType.returning": "Client de retour",
    "CustomerFile.clientType.loyal": "Client fidèle",
    "CustomerFile.clientType.flagged": "Signalé",
    "CustomerFile.clientType.blacklisted": "Blacklisté",
    "CustomerFile.blacklistedReadOnly": "Retirez ce client de la blacklist pour modifier son type",

    // Analytics
    "Analytics.searchCustomerPlaceholder": "Rechercher par nom, téléphone ou courriel...",

    "CustomerFile.tab.profile": "Profil",
    "CustomerFile.tab.communication": "Communication",
    "CustomerFile.tab.history": "Historique",
    "CustomerFile.tab.notes": "Notes & photos",
    "CustomerFile.section.identity": "Identité & coordonnées",
    "CustomerFile.section.identityDesc": "Informations essentielles et profil enrichi du client.",
    "CustomerFile.verifiedAccount.explain": "Ce client possède son propre compte. Modifier son courriel ici change les communications du salon, jamais son adresse de connexion.",
    "CustomerFile.kpi.reliability": "Fiabilité",
    "CustomerFile.kpi.spending": "Dépenses",
    "CustomerFile.daysShort": "j",
    "CustomerFile.histTab.appointments": "Rendez-vous",
    "CustomerFile.histTab.invoices": "Factures",
    "CustomerFile.histTab.products": "Produits",
    "CustomerFile.histTab.giftcards": "Cartes",
    "CustomerFile.histTab.balance": "Solde",
    "CustomerBalance.total": "Total dû",
    "CustomerBalance.empty": "Aucun montant dû.",
    "CustomerBalance.fromInvoice": "Facture {number}",
    "CustomerBalance.manual": "Saisie manuelle",
    "CustomerBalance.needsAttribution": "Facture partagée, à répartir",
    "CustomerBalance.mismatch": "La facture a bougé, répartition à revoir",
    "CustomerBalance.settle": "Régler",
    "CustomerBalance.split": "Répartir",
    "CustomerBalance.record": "Enregistrer un montant dû",
    "CustomerBalance.recordTitle": "Enregistrer un montant dû",
    "CustomerBalance.amount": "Montant",
    "CustomerBalance.reason": "Justification (obligatoire)",
    "CustomerBalance.reasonRequired": "Expliquez pourquoi ce montant est dû.",
    "CustomerBalance.amountInvalid": "Saisissez un montant supérieur à zéro.",
    "CustomerBalance.recording": "Enregistrement…",
    "CustomerBalance.recordSuccess": "Montant dû enregistré.",
    "CustomerBalance.recordFailed": "Impossible d'enregistrer le montant dû.",
    "CustomerBalance.cancelEntry": "Annuler ce montant",
    "CustomerBalance.cancelReason": "Pourquoi l'annuler ?",
    "CustomerBalance.cancelReasonRequired": "Expliquez pourquoi vous annulez ce montant.",
    "CustomerBalance.confirmCancel": "Confirmer l'annulation",
    "CustomerBalance.cancelling": "Annulation…",
    "CustomerBalance.cancelSuccess": "Montant annulé.",
    "CustomerBalance.cancelFailed": "Impossible d'annuler ce montant.",
    "CustomerBalance.settleTitle": "Régler ce montant",
    "CustomerBalance.remainingDue": "Restant dû :",
    "CustomerBalance.settling": "Règlement…",
    "CustomerBalance.settleSuccess": "Montant réglé.",
    "CustomerBalance.settleFailed": "Impossible de régler ce montant.",
    "CustomerBalance.noOpenRegister": "Ouvrez la caisse avant d'encaisser un règlement comptant.",
    "CustomerBalance.cashOtherLocation": "Cette dette appartient à {location}. Réglez-la par un autre mode de paiement ou depuis {location}.",
    "CustomerBalance.cashOtherLocationUnnamed": "Cette dette appartient à un autre emplacement. Réglez-la par un autre mode de paiement ou depuis cet emplacement.",
    "CustomerBalance.loadingRetry": "Chargement en cours, réessayez dans un instant.",
    "CustomerBalance.settlementFor": "Règlement de solde,",
    "CustomerBalance.settleInProgress": "Un règlement est déjà en cours.",
    "CustomerBalance.invoiceMoneyLoadFailed": "Impossible de charger les données de facturation de la facture, réessayez.",
    "CustomerBalance.residualNotCashable": "Ce résidu ne peut pas être encaissé en comptant, choisissez un autre mode de paiement.",
    "CustomerBalance.alreadySettled": "Cette ligne a déjà été réglée ailleurs, plus rien n'est dû.",
    "CustomerBalance.shareLoadFailed": "Impossible de charger la part de ce client, réessayez.",
    "CustomerBalance.entryLoadFailed": "Impossible de charger ce montant dû, réessayez.",
    "CustomerBalance.orphanCashMovement": "Un mouvement de caisse a été enregistré alors que le règlement n'a pas abouti. Corrigez le tiroir à la main, montant :",
    "CustomerBalance.splitTitle": "Répartir la facture partagée",
    "CustomerBalance.splitDesc": "La facture {number} sert plusieurs clients. Son solde n'est la dette de personne en particulier tant que vous n'avez pas dit qui doit quoi.",
    "CustomerBalance.splitInvoiceBalance": "Solde de la facture à répartir",
    "CustomerBalance.splitLinesHint": "Les montants des lignes sont affichés avant taxes, à titre indicatif. La répartition porte sur le solde de la facture ci-dessus.",
    "CustomerBalance.splitShare": "Part due",
    "CustomerBalance.splitAssigned": "Réparti",
    "CustomerBalance.splitRemainingToAssign": "Reste à répartir",
    "CustomerBalance.splitUnknownCustomer": "Client sans nom",
    "CustomerBalance.splitAction": "Enregistrer la répartition",
    "CustomerBalance.splitting": "Enregistrement…",
    "CustomerBalance.splitSuccess": "Répartition enregistrée.",
    "CustomerBalance.splitFailed": "Impossible d'enregistrer la répartition.",
    "CustomerBalance.splitGapNotZero": "La somme des parts doit faire exactement le solde de la facture.",
    "CustomerBalance.splitAmountInvalid": "Saisissez un montant valide pour chaque client.",
    "CustomerBalance.splitEmpty": "Attribuez un montant à au moins un client.",
    "CustomerBalance.splitInvoiceGone": "Cette facture n'existe plus.",
    "CustomerBalance.splitStaleBalance": "Le solde de la facture a changé, reprenez la répartition.",
    "CustomerBalance.splitReplaceFailed": "Les parts existantes n'ont pas pu être annulées, rouvrez cette fenêtre et recommencez.",
    "CustomerBalance.splitReplaceLost": "Les parts précédentes ont été annulées mais les nouvelles n'ont pas pu être enregistrées. La facture est redevenue non répartie, reprenez la répartition.",
    "CustomerBalance.splitInProgress": "Une répartition est déjà en cours d'enregistrement.",
    "CustomerBalance.splitRedoneReason": "Répartition refaite",
    "CustomerBalance.creditAvailable": "Crédit disponible",
    "CustomerBalance.creditNeverNets": "Un crédit ne se soustrait jamais tout seul du montant dû. Appliquez-le depuis une ligne ci-dessous.",
    "CustomerBalance.grantCredit": "Accorder un crédit",
    "CustomerBalance.applyCredit": "Appliquer un crédit",

    // ── Lot 2 credit client, tache 6 : depenser un credit sur une facture ──
    "StoreCredit.payWith": "Payer avec le crédit du dossier",
    "StoreCredit.dialogTitle": "Crédit au dossier",
    "StoreCredit.available": "Crédit disponible",
    "StoreCredit.amount": "Montant à appliquer",
    "StoreCredit.apply": "Appliquer",
    "StoreCredit.none": "Aucun crédit disponible pour les clients de cette facture.",
    "StoreCredit.nothingDue": "Cette facture ne doit plus rien, il n'y a rien à payer avec un crédit.",
    "StoreCredit.rowLocked": "Payé par un crédit au dossier. Cette ligne se retire en annulant l'avoir, pas d'ici.",
    "StoreCredit.staleCredit": "Ce crédit a changé depuis l'ouverture de cette fenêtre. Rouvrez-la et saisissez le montant à nouveau.",
    "StoreCredit.loadFailed": "Le crédit n'a pas pu être relu. Rien n'a été appliqué.",
    "StoreCredit.selectCredit": "Choisissez d'abord un crédit.",
    "StoreCredit.amountInvalid": "Saisissez un montant supérieur à zéro.",
    "StoreCredit.saveInvoiceFirst": "Enregistrez la facture avant d'y appliquer un crédit.",
    "StoreCredit.applied": "Crédit appliqué.",
    "StoreCredit.applyFailed": "Le crédit n'a pas pu être appliqué.",
    "StoreCredit.expiresOn": "Expire le",
    "StoreCredit.error.accessDenied": "Vous ne pouvez pas agir sur cet emplacement.",
    "StoreCredit.error.permissionDenied": "Vous n'avez pas la permission de gérer les crédits au dossier.",
    "StoreCredit.error.amountInvalid": "Saisissez un montant supérieur à zéro.",
    "StoreCredit.error.notFound": "Ce crédit n'existe plus.",
    "StoreCredit.error.otherBrand": "Ce crédit appartient à une autre entreprise.",
    "StoreCredit.originSalon": "Crédit émis à {salon}",
    "StoreCredit.error.deleted": "Ce crédit a été supprimé.",
    "StoreCredit.error.notStoreCredit": "Cet avoir a été remboursé, il n'y a rien à dépenser.",
    "StoreCredit.error.notSpendable": "Ce crédit ne peut pas être dépensé dans son état actuel.",
    "StoreCredit.error.expired": "Ce crédit est expiré.",
    "StoreCredit.error.insufficient": "Le crédit disponible ne couvre pas ce montant. Saisissez un montant plus petit.",
    "StoreCredit.error.aboveRemaining": "Ce crédit ne porte que {amount}. Saisissez ce montant ou moins.",
    "StoreCredit.error.originInvoice": "Ce crédit vient de cette facture même. Le dépenser ici le ferait disparaître sans rien rendre à la cliente.",
    "StoreCredit.error.invoiceNotFound": "Cette facture est introuvable.",
    "StoreCredit.error.customerMismatch": "Cette facture ne porte aucune ligne du client à qui ce crédit appartient.",
    "StoreCredit.error.exceedsInvoiceDue": "Le montant dépasse ce que cette facture doit encore.",
    "StoreCredit.error.exceedsCustomerShare": "Un avoir est nominatif : il ne règle que la part de son client sur cette facture. Saisissez un montant plus petit.",
    "StoreCredit.customerShareHint": "Part sur cette facture pour {name} : {amount}",
    "StoreCredit.error.manualPaymentRefused": "Un paiement par crédit au dossier ne se saisit pas à la main. Passez par le bouton de crédit du dossier.",
    "StoreCredit.error.lineImmutable": "Une ligne de paiement par crédit au dossier ne se modifie pas. Retirez la ligne, ou annulez l'avoir.",
    "StoreCredit.error.reversalLocked": "Cette ligne est le renversement d'un avoir. Elle se retire en annulant l'avoir, pas en supprimant le paiement.",
    "StoreCredit.error.backsSettlement": "Ce crédit a réglé un montant dû au dossier de la cliente. Retirer son paiement rendrait le crédit sans annuler le règlement.",
    "StoreCredit.error.entryNotFound": "Ce montant dû n'existe plus.",
    "StoreCredit.error.entryOtherSalon": "Ce montant dû appartient à un autre salon.",
    "StoreCredit.error.entryCancelled": "Ce montant dû a été annulé, il n'y a plus rien à régler.",
    "StoreCredit.error.entryDerived": "Ce montant vient d'une facture entière. Ouvrez la facture et appliquez-y le crédit.",
    "StoreCredit.error.entryCustomerMismatch": "Ce montant est dû par un autre client que celui de ce crédit.",
    "StoreCredit.error.exceedsDue": "Le montant dépasse ce qui reste dû sur cette ligne. Saisissez un montant plus petit.",
    "StoreCredit.error.goodwillCustomerNotFound": "Ce dossier client est introuvable.",
    "StoreCredit.error.goodwillCustomerNotInSalon": "Ce dossier n'a ni facture ni rendez-vous dans votre salon. Un crédit ne peut pas y être accordé.",
    // Lot A du 2026-09-27, tâche 9. Les trois phrases disent la même chose que
    // le `HINT` du serveur, que le front n'affiche pas. Relevées dans la
    // migration `20260927181000`.
    "StoreCredit.error.noCustomerOnLines": "Aucune ligne choisie ne porte de client : il n'y a pas de dossier à créditer. Choisissez « Rendre l'argent », ou ajoutez le client sur la facture.",
    "StoreCredit.error.customerNotOnLines": "Ce dossier ne figure sur aucune des lignes choisies. Choisissez les lignes du client à créditer.",
    "StoreCredit.error.cashRefundNeedsOpenRegister": "Aucune caisse n'est ouverte : un remboursement comptant ne laisserait aucune trace dans le tiroir. Ouvrez la caisse, ou choisissez la carte, le virement, ou créditez le dossier du client.",
    "StoreCredit.error.lineAlreadyCredited": "Une des lignes choisies a déjà été créditée. Décochez-la : une ligne ne se crédite qu'une fois.",
    "StoreCredit.error.tipOnlyOnFull": "Le pourboire ne se rembourse qu'avec l'avoir qui crédite toutes les lignes restantes, et une seule fois.",
    "StoreCredit.error.exceedsCollected": "Cet avoir rendrait plus que ce que la facture a encaissé. Maximum possible : {max}.",
    "StoreCredit.error.onlineRefundPending": "Un remboursement en ligne est en cours de traitement sur cette facture. Réessayez dans quelques minutes.",
    "StoreCredit.error.multipleCustomers": "Les lignes sélectionnées portent plusieurs clients. Un avoir ne vise qu'un dossier, sélectionnez les lignes d'un seul client.",
    "StoreCredit.error.alreadyCancelled": "Cet avoir est déjà annulé.",
    "StoreCredit.error.alreadyUsed": "Ce crédit a déjà été entamé, il ne peut plus être annulé.",
    "StoreCredit.error.cancelReasonRequired": "Annuler un crédit exige un motif écrit.",
    "StoreCredit.error.undoReasonRequired": "Défaire une application de crédit exige un motif écrit.",
    "StoreCredit.error.redemptionNotFound": "Cette application de crédit n'existe plus.",
    "StoreCredit.error.redemptionOtherSalon": "Cette application de crédit appartient à un autre salon.",
    "StoreCredit.error.redemptionNotAUse": "Seul un crédit dépensé se défait, pas une restitution.",
    "StoreCredit.error.redemptionAlreadyRestored": "Cette application a déjà été défaite, le crédit est revenu au dossier.",
    "StoreCredit.error.redemptionLinkMissing": "Cette application ne porte ni encaissement ni règlement à défaire. Elle a été enregistrée avant cette fonctionnalité, elle doit donc être corrigée à la main.",
    "StoreCredit.error.entryBelowSettled": "Ce montant dû est déjà réglé au-delà du nouveau chiffre. Défaites d'abord un règlement.",
    "CustomerCredits.title": "Crédits du dossier",
    "CustomerCredits.empty": "Ce dossier n'a jamais porté de crédit.",
    "CustomerCredits.ofTotal": "sur {total} accordés",
    "CustomerCredits.applications": "Appliqué à",
    "CustomerCredits.undo": "Défaire",
    "CustomerCredits.undoTitle": "Défaire cette application de crédit",
    "CustomerCredits.undoDescription": "{amount} reviennent au dossier, et le montant qu'ils avaient réglé redevient dû.",
    "CustomerCredits.undoWarning": "La facture ou le montant dû que ce crédit avait réglé remonte d'autant. L'avoir garde une trace permanente de cette reprise, avec votre motif.",
    "CustomerCredits.undoing": "Reprise en cours…",
    "CustomerCredits.undoSuccess": "Application de crédit défaite.",
    "CustomerCredits.undoFailed": "Impossible de défaire cette application de crédit.",
    "CustomerCredits.cancel": "Annuler ce crédit",
    "CustomerCredits.cancelTitle": "Annuler ce crédit",
    "CustomerCredits.cancelDescription": "La totalité, soit {amount}, est reprise au dossier.",
    "CustomerCredits.cancelWarning": "La ligne de renversement posée sur la facture d'origine est retirée, et le stock que cet avoir avait remis en ressort. C'est irréversible.",
    "CustomerCredits.cancelling": "Annulation…",
    "CustomerCredits.cancelSuccess": "Crédit annulé.",
    "CustomerCredits.cancelFailed": "Impossible d'annuler ce crédit.",
    "CustomerCredits.cancelOtherLocation": "Émis à {salon} : il s'annule depuis cet emplacement.",
    "CustomerCredits.undoOtherLocation": "Appliqué à {salon} : il se défait depuis cet emplacement.",
    "CustomerCredits.cancelBlocked": "Ce crédit a été entamé. Défaites d'abord ses applications, ou enregistrez ce qui est dû comme montant manuel.",
    "CustomerCredits.reasonRequired": "Écrivez le motif.",
    "CustomerCredits.reasonPlaceholder": "Motif (obligatoire)",
    "CustomerCredits.hidden": "Les crédits de la fiche ne s'affichent qu'avec l'accès au module Facturation ou l'ouverture des fiches clients.",
    "CustomerCredits.notUndoable": "Non défaisable",
    "GrantCredit.title": "Accorder un crédit au dossier",
    "GrantCredit.description": "Cet argent est offert. Il reste au dossier de la cliente et se dépensera sur une facture ultérieure.",
    "GrantCredit.amount": "Montant accordé",
    "GrantCredit.reason": "Motif",
    "GrantCredit.details": "Justification (obligatoire)",
    "GrantCredit.detailsRequired": "Expliquez pourquoi ce crédit est accordé.",
    "GrantCredit.amountInvalid": "Saisissez un montant supérieur à zéro.",
    "GrantCredit.submit": "Accorder le crédit",
    "GrantCredit.submitting": "Enregistrement…",
    "GrantCredit.success": "Crédit accordé.",
    "GrantCredit.failed": "Impossible d'accorder le crédit.",
    "GrantCredit.expiresAt": "Date d'expiration",
    "GrantCredit.expiresNever": "Aucune expiration",
    "GrantCredit.expiresWarning": "Les crédits accordés ici n'expirent jamais, et c'est volontaire. Un crédit issu d'un retour ressemble de très près à une carte prépayée, et la Loi sur la protection du consommateur encadre étroitement leur péremption au Québec. Ce champ ne s'ouvrira qu'après validation juridique.",
    "ApplyCredit.title": "Appliquer un crédit à ce montant",
    "ApplyCredit.none": "Cette cliente n'a plus aucun crédit à dépenser.",
    "ApplyCredit.nothingDue": "Cette ligne ne doit plus rien, il n'y a rien à créditer.",
    "ApplyCredit.applying": "Application…",
    "ApplyCredit.success": "Crédit appliqué au montant dû.",
    "ApplyCredit.failed": "Impossible d'appliquer le crédit.",
    "CustomerFile.section.reliability": "Historique & fiabilité",
    "CustomerFile.totalAppointments": "Total RDV",
    "CustomerFile.presentCount": "Présent",
    "CustomerFile.noshowCount": "No-show",
    "CustomerFile.reliabilityRate": "Taux de fiabilité",
    "CustomerFile.totalSpending": "Dépenses totales",
    "CustomerFile.averageBasket": "Panier moyen",
    "CustomerFile.visitFrequency": "Fréquence de visite",
    "CustomerFile.weeksAbbr": "sem",
    "CustomerFile.lastVisit": "Dernière visite",
    "CustomerFile.daysAgo": "jours",
    "CustomerFile.today": "aujourd'hui",
    "CustomerFile.never": "Jamais",
    "CustomerFile.inactiveWarning": "Inactif > 3 mois",
    "Customer.lastVisitFilter.label": "Dernière visite",
    "Customer.lastVisitFilter.all": "Tous",
    "Customer.lastVisitFilter.7d": "7 derniers jours",
    "Customer.lastVisitFilter.30d": "30 derniers jours",
    "Customer.lastVisitFilter.90d": "90 derniers jours",
    "Customer.lastVisitFilter.6m": "6 derniers mois",
    "Customer.lastVisitFilter.1y": "Dernière année",
    "Customer.lastVisitFilter.2y": "2 dernières années",
    "Customer.lastVisitFilter.5y": "5 dernières années",
    "Customer.lastVisitFilter.10y": "10 dernières années",
    "Customer.lastVisitFilter.never": "Jamais venu",
    "Customer.statusFilter.label": "Statut",
    "Customer.statusFilter.all": "Tous les statuts",
    "Customer.statusFilter.blacklisted": "Liste noire",
    "Analytics.noClientsMatchFilter": "Aucun client ne correspond à ce filtre.",
    "CustomerFile.autoClassified": "(calculé automatiquement)",
    "CustomerFile.phoneNotProvided": "Aucun téléphone",
    "CustomerFile.phoneActions.menuLabel": "Actions pour le numéro de téléphone",
    "CustomerFile.phoneActions.call": "Appeler",
    "CustomerFile.phoneActions.text": "Texter",
    "CustomerFile.phoneActions.copy": "Copier le numéro",
    "CustomerFile.phoneActions.copied": "Numéro copié",
    "CustomerFile.phoneActions.copyFailed": "Impossible de copier le numéro",
    "CustomerFile.smsNotDelivered": "Ce numéro ne reçoit pas les textos",
    "CustomerFile.contactMasked": "Coordonnées masquées",

    // Customer Files
    "CustomerFile.loading": "Chargement...",
    "CustomerFile.section.essential": "Informations essentielles",
    "CustomerFile.section.safety": "Sécurité & personnalisation",
    "CustomerFile.section.technicalSheet": "Fiche technique",
    "CustomerFile.section.history": "Suivi visuel",
    "CustomerFile.section.notes": "Notes",
    "CustomerFile.allergies": "Allergies / Sensibilités",
    "CustomerFile.allergiesPlaceholder": "ex: PPD, parfum, latex...",
    "CustomerFile.preferredStylist": "Employé(e) préféré(e)",
    "CustomerFile.selectStylist": "Sélectionner un(e) employé(e)",
    "CustomerFile.none": "Aucun",
    "CustomerFile.technicalSheet.add": "Fiche",
    "CustomerFile.technicalSheet.empty": "Aucune fiche technique",
    "CustomerFile.technicalSheet.date": "Date",
    "CustomerFile.technicalSheet.employee": "Employé",
    "CustomerFile.technicalSheet.selectEmployee": "Sélectionner un employé",
    "CustomerFile.technicalSheet.note": "Note",
    "CustomerFile.technicalSheet.notePlaceholder": "Décrivez le service technique, la formule, le résultat...",
    "CustomerFile.technicalSheet.save": "Enregistrer la fiche",
    "CustomerFile.technicalSheet.lockedHint": "Une fiche enregistrée ne peut plus être modifiée.",
    "CustomerFile.technicalSheet.addedOn": "Ajoutée le",
    "CustomerFile.technicalSheet.migrated": "Migrée",
    "CustomerFile.technicalSheet.delete": "Supprimer",
    "CustomerFile.technicalSheet.deleteConfirm": "Supprimer cette fiche définitivement ? Cette action est irréversible.",
    "CustomerFile.technicalSheet.saved": "Fiche enregistrée",
    "CustomerFile.technicalSheet.deleted": "Fiche supprimée",
    "CustomerFile.technicalSheet.saveFailed": "Impossible d'enregistrer la fiche",
    "CustomerFile.technicalSheet.deleteFailed": "Impossible de supprimer la fiche",
    "CustomerFile.technicalSheet.employeeRequired": "Sélectionnez un employé.",
    "CustomerFile.technicalSheet.noteRequired": "La note ne peut pas être vide.",
    "CustomerFile.technicalSheet.selectDate": "Sélectionner une date",
    "CustomerFile.technicalSheet.edit": "Modifier la note",
    "CustomerFile.technicalSheet.editSave": "Enregistrer la correction",
    "CustomerFile.technicalSheet.edited": "Modifiée le",
    "CustomerFile.technicalSheet.editedBy": "par",
    "CustomerFile.technicalSheet.editSaved": "Fiche corrigée",
    "CustomerFile.technicalSheet.editFailed": "Impossible de corriger la fiche",
    "CustomerFile.preferredProducts": "Produits / marques préférés",
    "CustomerFile.preferredProductsPlaceholder": "Produits que le client aime ou à éviter...",
    "CustomerFile.hairPhotos": "Photos client (max 6)",
    "CustomerFile.uploadPhoto": "Télécharger photo",
    "CustomerFile.uploading": "Téléchargement...",
    "CustomerFile.photoUploaded": "Photo téléchargée avec succès",
    "CustomerFile.photoDeleted": "Photo supprimée avec succès",
    "CustomerFile.maxPhotos": "Maximum 6 photos autorisées",
    "CustomerFile.pendingPhoto": "Photo à enregistrer",
    "CustomerFile.pendingPhotoRemove": "Retirer cette photo",
    "CustomerFile.photoRemove": "Retirer cette photo",
    "CustomerFile.appointmentHistory": "Historique des rendez-vous",
    "CustomerFile.noAppointments": "Aucun rendez-vous précédent",
    "CustomerFile.productPurchaseHistory": "Historique des achats produits",
    "CustomerFile.invoiceHistory": "Historique des factures",
    "CustomerFile.noInvoices": "Aucune facture enregistrée.",
    "CustomerFile.invoiceItemsMore": "+{count} autres",
    "CustomerFile.giftCardHistory": "Cartes cadeaux",
    "CustomerFile.noGiftCards": "Aucune carte cadeau liée à ce client.",
    "CustomerFile.beneficiary": "Bénéficiaire",
    "CustomerFile.noProductPurchases": "Aucun achat de produit enregistré.",
    "CustomerFile.commPreferences": "Préférences de communication",
    "CustomerFile.smsNotifications": "Notifications SMS",
    "CustomerFile.emailNotifications": "Notifications email",
    "CustomerFile.optOutMarketing": "Désactiver le marketing",
    "CustomerFile.disableAllNotifications": "Désactiver toutes les notifications",
    "CustomerFile.disableAllNotificationsHelp": "Aucun courriel ni message texte ne sera envoyé à ce client, y compris les confirmations et les rappels de rendez-vous.",
    "CustomerFile.marketing.section": "Marketing / Infolettre",
    "CustomerFile.marketing.emailLabel": "Recevoir les courriels marketing",
    "CustomerFile.marketing.helper": "Indépendant des notifications de rendez-vous",
    "Blacklist.title": "Blacklist",
    "Blacklist.blockedClients": "Clients bloqués",
    "Blacklist.addClient": "Ajouter un client à la blacklist",
    "Blacklist.removeClient": "Retirer",
    "Blacklist.removeConfirm": "Retirer ce client de la blacklist ?",
    "Blacklist.removeConfirmDescription": "Ce client pourra à nouveau réserver en ligne.",
    "Blacklist.reason": "Raison (optionnelle)",
    "Blacklist.reasonPlaceholder": "Ex : Ne se présente jamais aux rendez-vous",
    "Blacklist.addedOn": "Ajouté le",
    "Blacklist.employeeReadOnly": "Seul un propriétaire peut modifier cette liste",
    "Blacklist.searchClient": "Rechercher un client...",
    "Blacklist.bookingBlocked": "La réservation en ligne est désactivée. Si vous souhaitez prendre rendez-vous, veuillez nous contacter au salon.",
    "Blacklist.noClients": "Aucun client dans la blacklist",
    "Blacklist.emptyDescription": "Ajoutez des clients pour les empêcher de réserver en ligne",
    "Blacklist.confirmAdd": "Ajouter à la blacklist",
    "Blacklist.adding": "Ajout...",
    "Blacklist.clientAdded": "Client ajouté à la blacklist",
    "Blacklist.clientRemoved": "Client retiré de la blacklist",
    "Blacklist.blockedCountOne": "{count} client bloqué",
    "Blacklist.blockedCountMany": "{count} clients bloqués",
    "Blacklist.pickClientFirst": "Choisissez d'abord un client.",
    "Blacklist.removing": "Retrait...",
    "CustomerFile.notifDialog.title.email": "Préférences de notifications par courriel",
    "CustomerFile.notifDialog.title.sms": "Préférences de notifications par SMS",
    "CustomerFile.notifDialog.selectAll": "Tout sélectionner",
    "CustomerFile.notifDialog.section.appointments": "Rendez-vous",
    "CustomerFile.notifDialog.section.series": "Séries récurrentes",
    "CustomerFile.notifDialog.section.group": "Multi-services",
    "CustomerFile.notifDialog.section.reminders": "Rappels",
    "CustomerFile.notifDialog.appointments.pending": "Demande en attente",
    "CustomerFile.notifDialog.appointments.confirmation": "Approbation d'une demande en attente",
    "CustomerFile.notifDialog.appointments.book": "Confirmation de réservation instantanée",
    "CustomerFile.notifDialog.appointments.reschedule": "Reporté",
    "CustomerFile.notifDialog.appointments.cancel": "Annulé",
    "CustomerFile.notifDialog.appointments.duration_change": "Durée modifiée",
    "CustomerFile.notifDialog.series.pending": "Série en attente",
    "CustomerFile.notifDialog.series.approved": "Série approuvée",
    "CustomerFile.notifDialog.series.rejected": "Série non disponible",
    "CustomerFile.notifDialog.group.pending": "Multi-services en attente",
    "CustomerFile.notifDialog.group.approved": "Multi-services confirmé",
    "CustomerFile.notifDialog.reminders.24h": "Rappel 24 heures",
    "CustomerFile.notifDialog.reminders.48h": "Rappel 48 heures",
    "CustomerFile.notifDialog.reminders.72h": "Rappel 72 heures",
    "CustomerFile.notifDialog.reminders.1h": "Rappel 1 heure",
    "CustomerFile.notifDialog.confirm": "Confirmer",
    "CustomerFile.internalNotes": "Notes internes (personnel seulement)",
    "CustomerFile.internalNotesPlaceholder": "Notes privées pour le personnel...",
    "CustomerFile.save": "Enregistrer",
    "CustomerFile.cancel": "Annuler",
    "CustomerFile.saving": "Enregistrement...",
    "CustomerFile.success": "Succès",
    "CustomerFile.fileUpdated": "Dossier client mis à jour",
    "CustomerFile.error": "Erreur",
    "CustomerFile.invalidImageFile": "Veuillez sélectionner un fichier image valide",
    "CustomerFile.fileTooLarge": "La taille du fichier doit être inférieure à 5 Mo",
    "CustomerFile.service": "Service",
    "CustomerFile.status.confirmed": "Confirmé",
    "CustomerFile.status.completed": "Terminé",
    "CustomerFile.status.cancelled": "Annulé",
    "CustomerFile.status.pending": "En attente",
    "CustomerFile.loadMore": "Charger plus",
    "CustomerFile.remaining": "restants",
    "CustomerFile.remainingAppointments": "rendez-vous restants",
    "CustomerFile.cancelledHidden": "rendez-vous annulés masqués",
    // Rappel des prochains rendez-vous, remis ou envoyé à la cliente au comptoir.
    "CustomerFile.upcoming.button": "Prochains rendez-vous",
    "CustomerFile.upcoming.print": "Imprimer",
    "CustomerFile.upcoming.email": "Envoyer par courriel",
    "CustomerFile.upcoming.dialogTitle": "Envoyer les prochains rendez-vous",
    "CustomerFile.upcoming.recipient": "Destinataire",
    "CustomerFile.upcoming.addressOnFile": "Adresse enregistrée sur la fiche",
    "CustomerFile.upcoming.maskedHint": "Vous n'avez pas accès à l'adresse de cette cliente. Le rappel partira à l'adresse enregistrée sur sa fiche.",
    "CustomerFile.upcoming.mutedHint": "Cette cliente a désactivé les courriels. Vous demandez ce rappel vous-même, il partira quand même.",
    "CustomerFile.upcoming.attachmentsNote": "Chaque rendez-vous est joint en fichier agenda, pour que la cliente l'ajoute d'un geste.",
    "CustomerFile.upcoming.cancel": "Annuler",
    "CustomerFile.upcoming.send": "Envoyer",
    "CustomerFile.upcoming.sending": "Envoi en cours",
    "CustomerFile.upcoming.sent": "Rappel envoyé",
    "CustomerFile.upcoming.sendFailed": "Le rappel n'a pas pu être envoyé",
    "CustomerFile.upcoming.preparing": "Préparation",
    "CustomerFile.upcoming.none": "Cette cliente n'a aucun rendez-vous à venir",
    "CustomerFile.upcoming.printFailed": "Rien n'est parti vers l'imprimante",
    // Enriched profile fields (Task 7)
    "CustomerFile.emergencyContact": "Contact d'urgence",
    "CustomerFile.consent.section": "Loi 25 : Consentement aux données",
    "CustomerFile.consent.section.generic": "Consentement à la collecte des données",
    "CustomerFile.consent.label": "Consentement obtenu du client",
    "CustomerFile.consent.dateLabel": "Date de consentement",
    "CustomerFile.consent.collectedOn": "Recueilli le {date} par {who}",
    "CustomerFile.familyTitle": "Famille",
    "CustomerFile.roleConjoint": "Conjoint(e)",
    "CustomerFile.roleEnfant": "Enfant",
    "CustomerFile.roleParent": "Parent",
    "CustomerFile.roleFratrie": "Frère / Sœur",
    "CustomerFile.roleGrandparent": "Grand-parent",
    "CustomerFile.roleGrandchild": "Petit-enfant",
    "CustomerFile.roleAutre": "Autre membre de la famille",
    "CustomerFile.addMember": "Ajouter un membre",
    "CustomerFile.selectClient": "Choisir un client...",
    "CustomerFile.selectRelationship": "Relation...",
    "CustomerFile.alreadyLinked": "Ce lien existe déjà",
    "CustomerFile.removeMember": "Retirer ce membre",
    "CustomerFile.familyEmpty": "Aucun membre de la famille lié",
    "CustomerFile.familyEmptyHelp": "Liez les proches pour retrouver facilement leurs rendez-vous et leurs dossiers depuis l'un ou l'autre.",
    "CustomerFile.familyEmptyReadOnly": "Aucun proche n'est lié à ce client. Seule une personne autorisée à gérer le répertoire peut en ajouter.",
    "CustomerFile.familyPickClient": "Choisissez d'abord un client.",
    "CustomerFile.familyPickRole": "Choisissez d'abord le lien de parenté.",
    "CustomerFile.familyPending": "À enregistrer",
    "CustomerFile.familySearchPlaceholder": "Rechercher par nom, téléphone ou courriel...",

    // Analytics
    "Analytics.title": "Analyses",
    "Analytics.revenue": "Revenus",
    "Analytics.totalRevenue": "Revenu total",
    "Analytics.completedAppointment": "rendez-vous enregistré",
    "Analytics.completedAppointments": "rendez-vous enregistrés",
    "Analytics.averagePerAppointment": "Revenu moyen par rendez-vous",
    "Analytics.cancellationLosses": "Pertes liées aux annulations",
    "Analytics.cancelledSingular": "annulé",
    "Analytics.cancelled": "annulés",
    "Analytics.servicePerformance": "Performance des services",
    "Analytics.customerBase": "Base de clients",
    "Analytics.preferredClients": "Répertoire clients",
    "Analytics.clientsWhoChose": "Clients qui ont choisi ce salon",
    "Analytics.allClients": "Tous les clients qui ont choisi ce salon",
    "Analytics.noClientsYet": "Aucun client dans ce salon.",
    "Analytics.noCustomersFound": "Aucun client trouvé.",
    "Analytics.loadFailed": "La liste des clients n'a pas pu être chargée.",
    "Analytics.retry": "Réessayer",
    "Analytics.directorySubtitleOne": "{count} client dans ce salon",
    "Analytics.directorySubtitleMany": "{count} clients dans ce salon",
    "Analytics.directorySubtitleOneAllLocations": "{count} client dans tous vos emplacements",
    "Analytics.directorySubtitleManyAllLocations": "{count} clients dans tous vos emplacements",
    "Analytics.directorySubtitleOneAt": "{count} client rattaché à {location}",
    "Analytics.directorySubtitleManyAt": "{count} clients rattachés à {location}",
    "Analytics.directorySubtitleAtUnknown": "Clients rattachés à {location}",
    "Analytics.directorySubtitleFilteredOne": "{count} client correspond à ce filtre",
    "Analytics.directorySubtitleFilteredMany": "{count} clients correspondent à ce filtre",
    "Analytics.pagerPrevious": "Page précédente",
    "Analytics.pagerNext": "Page suivante",
    "Analytics.pagerPage": "Page {page}",
    "Analytics.pagerRange": "{from} à {to} sur {total}",
    "Analytics.name": "Nom",
    "Analytics.noName": "Sans nom",
    "Analytics.clientsModuleSubtitle": "Gérez votre clientèle et vos dossiers",
    "Analytics.kpiTotalClients": "Total clients",
    "Analytics.kpiTotalClientsBrand": "Total clients de la marque",
    "Analytics.kpiLoyal": "Fidèles",
    "Analytics.kpiFlagged": "Signalés",
    "Analytics.email": "Email",
    "Analytics.phone": "Téléphone",
    "Analytics.viewFile": "Voir le fichier",
    "Analytics.close": "Fermer",
    "Analytics.thisWeek": "Cette semaine",
    "Analytics.thisMonth": "Ce mois",
    "Analytics.custom": "Personnalisé",
    "Analytics.periodLabel": "Période",
    "Analytics.selectStartDate": "Date de début",
    "Analytics.selectEndDate": "Date de fin",

    // Add Client Dialog
    "AddClient.title": "Ajouter un client",
    "AddClient.description":
      "Créez un profil client pour permettre à votre client de recevoir des rendez-vous avant qu'il ne crée son propre compte.",
    "AddClient.addButton": "Ajouter",
    "AddClient.addFirstClient": "Ajouter votre premier client",
    "AddClient.fullName": "Nom complet",
    "AddClient.email": "Courriel",
    "AddClient.phone": "Téléphone",
    "AddClient.cancel": "Annuler",
    "AddClient.create": "Créer le profil",
    "AddClient.errorNameRequired": "Le nom est requis",
    "AddClient.errorNameTooShort": "Le nom doit contenir au moins 2 caractères",
    "AddClient.errorEmailRequired": "Le courriel est requis",
    "AddClient.errorEmailInvalid": "Courriel invalide",
    "AddClient.errorPhoneRequired": "Le téléphone est requis",
    "AddClient.errorPhoneTooShort": "Le numéro de téléphone doit contenir au moins 10 chiffres",
    "AddClient.errorPhoneInvalid": "Le numéro de téléphone est invalide",
    "AddClient.emailHint": "Requis pour les rappels par courriel",
    "AddClient.phoneHint": "Requis pour les rappels par message texte",
    "AddClient.noContactWarning": "Sans courriel ni téléphone, ce client ne recevra aucune notification (confirmations, rappels).",
    "AddClient.errorEmailTaken": "Cette adresse courriel est déjà utilisée par {name} dans ce salon.",
    "AddClient.createAnyway": "Créer quand même",
    "AddClient.duplicatePhoneTitle": "Numéro de téléphone existant",
    "AddClient.duplicatePhoneBodyOne": "Le numéro {phone} est déjà utilisé par « {name} ». Créer quand même un nouveau profil avec ce numéro ? (Par exemple, pour un membre de la famille.)",
    "AddClient.duplicatePhoneBodyMany": "Le numéro {phone} est déjà utilisé par {count} clients (dont « {name} »). Créer quand même un nouveau profil avec ce numéro ?",
    "AddClient.duplicateEmailTitle": "Adresse courriel existante",
    "AddClient.duplicateEmailBodyOne": "L'adresse {email} est déjà utilisée par « {name} ». Créer quand même un nouveau profil avec cette adresse ?",
    "AddClient.duplicateEmailBodyMany": "L'adresse {email} est déjà utilisée par {count} clients (dont « {name} »). Créer quand même un nouveau profil avec cette adresse ?",
    "AddClient.errorNotAuthenticated": "Vous devez être connecté",
    "AddClient.errorDuplicate": "Un client avec cet email existe déjà pour votre salon",
    "AddClient.errorGeneric": "Une erreur s'est produite lors de la création du profil",
    "AddClient.errorNoPermission": "Vous n'avez pas la permission d'ajouter un client. Demandez-la au propriétaire du salon.",
    "AddClient.successCreated": "Profil client créé avec succès",
    "AddClient.badgeCreatedByYou": "Créé par vous",
    "AddClient.badgeVerified": "Compte vérifié",
    "AddClient.editClient": "Modifier le client",
    "AddClient.editClientDesc": "Mettre à jour les informations du client",
    "AddClient.update": "Mettre à jour",
    "AddClient.updating": "Mise à jour...",
    "AddClient.updateSuccess": "Client mis à jour",
    "AddClient.updateSuccessDesc": "Les informations du client ont été mises à jour",
    "AddClient.updateError": "Échec de la mise à jour",
    "AddClient.deleteConfirmTitle": "Supprimer le client?",
    "AddClient.deleteConfirmDesc": "Êtes-vous sûr de vouloir supprimer ce client? Cette action est irréversible.",
    "AddClient.deleteConfirm": "Supprimer",
    "AddClient.deleteCancel": "Annuler",
    "AddClient.deleteSuccess": "Client supprimé",
    "AddClient.deleteSuccessDesc": "Le client a été supprimé",
    "AddClient.deleteError": "Échec de la suppression",
    "AddClient.deleteErrorHasRecords": "Suppression impossible",
    "AddClient.deleteErrorHasRecordsDesc": "Ce client a des factures ou des rendez-vous associés. Veuillez d'abord supprimer ces éléments.",
    "AddClient.deleteErrorForbidden": "Vous n'avez pas la permission de supprimer un client.",
    "AddClient.deleteErrorRetry": "Le serveur n'a pas répondu. Veuillez réessayer.",
    "AddClient.fullNamePlaceholder": "Entrez le nom complet",
    "AddClient.phonePlaceholder": "Entrez le numéro de téléphone",
    "AddClient.emailPlaceholder": "Entrez l'adresse courriel",
    "AddClient.googleEmailReadonly": "Ce courriel est lié à un compte Google et ne peut pas être modifié.",
    "AddClient.language": "Langue",
    "AddClient.languagePlaceholder": "Sélectionner la langue",
    "AddClient.languageFr": "Français",
    "AddClient.languageEn": "Anglais",
    "AddClient.birthday": "Anniversaire",
    "AddClient.birthdayDay": "Jour",
    "AddClient.birthdayMonth": "Mois",
    "AddClient.birthdayNone": "Non précisé",
    "AddClient.errorBirthdayIncomplete": "Choisissez le jour et le mois de l'anniversaire.",
    "AddClient.errorBirthdayImpossible": "Ce jour n'existe pas dans le mois choisi.",
    "AddClient.gender": "Sexe",
    "AddClient.genderPlaceholder": "Sélectionner",
    "AddClient.genderMale": "Homme",
    "AddClient.genderFemale": "Femme",
    "AddClient.genderOther": "Autre",
    "AddClient.genderUndisclosed": "Préfère ne pas répondre",
    "AddClient.secondaryPhone": "Téléphone secondaire",
    "AddClient.secondaryPhoneInvalid": "Le téléphone secondaire est invalide",
    "AddClient.emergencyContactName": "Nom du contact d'urgence",
    "AddClient.emergencyContactPhone": "Téléphone du contact d'urgence",
    "AddClient.emergencyContactPhoneInvalid": "Le téléphone du contact d'urgence est invalide",
    "AddClient.emergencyContactRelation": "Lien de parenté",
    "AddClient.acquisitionSource": "Comment a-t-il connu le salon ?",
    "AddClient.acquisitionSourcePlaceholder": "Sélectionner la source",
    "AddClient.acquisitionSourceGoogle": "Google",
    "AddClient.acquisitionSourceSocial": "Réseaux sociaux",
    "AddClient.acquisitionSourceReferral": "Référence",
    "AddClient.acquisitionSourceWalkin": "Passage spontané",
    "AddClient.acquisitionSourceOnlineBooking": "Réservation en ligne",
    "AddClient.acquisitionSourceCompetitor": "Ancien client (concurrent)",
    "AddClient.acquisitionSourceOther": "Autre",
    "AddClient.acquisitionSourceOtherPlaceholder": "Précisez...",
    "AddClient.consentLabel": "Le client a consenti à la collecte de ses données (Loi 25)",
    "AddClient.consentLabel.generic": "Le client a consenti à la collecte de ses données",
    "AddClient.consentDate": "Date du consentement",
    "Analytics.voiceAIMetrics": "Métriques IA vocale",
    "Analytics.upgradeForAIMetrics": "Passez au plan Entreprise pour accéder aux métriques IA vocale",
    "Analytics.totalAICalls": "Total appels IA",
    "Analytics.aiSuccessRate": "Taux de succès",
    "Analytics.aiBookings": "Réservations IA",
    "Analytics.aiReschedules": "Reprogrammations IA",
    "Analytics.aiCancellations": "Annulations IA",
    "Analytics.onlineMetrics": "Métriques en ligne",
    "Analytics.onlineBookings": "Réservations en ligne",
    "Analytics.onlineReschedules": "Reprogrammations en ligne",
    "Analytics.onlineCancellations": "Annulations en ligne",
    "Analytics.directBookingMetrics": "Métriques de réservation directe (QR + Lien)",
    "Analytics.directTotalVisits": "Nombre de visites",
    "Analytics.directTotalScans": "Clics totaux sur le lien",
    "Analytics.directConversions": "Conversions",
    "Analytics.directConversionRate": "Taux de conversion",
    "Analytics.qrMetrics": "Métriques du code QR",
    "Analytics.qrTotalScans": "Scans totaux",
    "Analytics.qrConversions": "Conversions",
    "Analytics.qrConversionRate": "Taux de conversion",
    "Analytics.aiCallAnalytics": "Analyses des appels IA",
    "Analytics.totalCalls": "Appels totaux",
    "Analytics.successRate": "Taux de succès",
    "Analytics.avgCallDuration": "Durée moyenne d'appel",
    "Analytics.seconds": "secondes",
    "Analytics.totalCallTime": "Durée totale d'appel",

    // Duplicate customers
    "Duplicates.bannerTitle": "Il semble y avoir {count} doublon(s).",
    "Duplicates.bannerAction": "Vérifier",
    "Duplicates.reviewTitle": "Révision des doublons",
    "Duplicates.pairCount": "Paire {current} sur {total}",
    "Duplicates.matchEmail": "Même courriel",
    "Duplicates.matchPhone": "Même téléphone",
    "Duplicates.matchName": "Même nom",
    "Duplicates.appointment": "rendez-vous",
    "Duplicates.appointments": "rendez-vous",
    "Duplicates.lastVisit": "Dernière visite :",
    "Duplicates.unknownName": "Inconnu",
    "Duplicates.master": "Principal",
    "Duplicates.merge": "Fusionner",
    "Duplicates.notDuplicate": "Pas un doublon",
    "Duplicates.skip": "Passer",
    "Duplicates.chooseMaster": "Choisir le dossier principal",
    "Duplicates.chooseMasterDesc": "Les rendez-vous et les notes du doublon seront transférés au dossier principal. Le dossier doublon sera supprimé.",
    "Duplicates.mergeWarning": "Cette action est irréversible. Le dossier client doublon sera supprimé définitivement après la fusion.",
    "Duplicates.confirmMerge": "Confirmer la fusion",
    "Duplicates.mergeSuccess": "Clients fusionnés",
    "Duplicates.mergeSuccessDesc": "Le doublon a été fusionné avec succès.",
    "Duplicates.mergeOtherBrand": "Cette cliente a aussi un historique dans un autre salon : la fusion est impossible.",
    "Customer.toast.notInBrand": "Ce dossier client n'appartient pas à votre salon. Aucune modification n'a été enregistrée.",
    "Duplicates.dismissed": "Marqué comme non doublon",
    "Duplicates.noPending": "Aucun doublon à réviser",
    "Duplicates.noPendingDesc": "Tous les doublons potentiels ont été révisés. Vous pouvez lancer un nouveau scan pour vérifier de nouveau.",
    "Duplicates.scanComplete": "Scan terminé",
    "Duplicates.findButton": "Rechercher les doublons",
    "Duplicates.scanRunning": "Analyse en cours…",
    "Duplicates.scanStartedHint": "L'analyse prend généralement moins d'une minute. Vous pouvez continuer à travailler.",
    "Duplicates.scanResultCount": "{count} doublon(s) potentiel(s) trouvé(s).",
    "Duplicates.scanResultNone": "Aucun doublon trouvé. Votre répertoire est propre.",
    "Duplicates.scanResultReview": "Vérifier",
    "Duplicates.scanFailed": "L'analyse n'a pas pu aboutir. Réessayez dans quelques minutes.",
    "Duplicates.dialogTitle": "Doublons de clients",
  },
};
